import {
  getBillingDocumentByAdmissionAndSource,
  getBillingDocumentByConsumableCharge,
  getBillingDocumentByImagingExam,
  getBillingDocumentByLabOrder,
  getBillingDocumentBySurgeryCase,
  listBillingDocumentsByStay,
} from "./billing-documents/billing-documents-store";
import type {
  BillingDocument,
  BillingDocumentSource,
  BillingDocumentStatus,
} from "./billing-documents/types";
import { BILLING_DOC_STATUS_LABELS } from "./billing-documents/types";

export interface ServiceBillingSnapshot {
  documentId: string | null;
  status: BillingDocumentStatus | null;
  statusLabel: string;
  amount: number | null;
  hasDocument: boolean;
  /** Nombre de nuitées facturées (hospitalisation). */
  nightCount?: number;
  /** Montant cumulé des nuitées. */
  totalAmount?: number;
}

function fromDocument(doc: BillingDocument | null): ServiceBillingSnapshot {
  if (!doc) {
    return {
      documentId: null,
      status: null,
      statusLabel: "Non facturé",
      amount: null,
      hasDocument: false,
    };
  }
  return {
    documentId: doc.id,
    status: doc.status,
    statusLabel: BILLING_DOC_STATUS_LABELS[doc.status],
    amount: doc.subtotal,
    hasDocument: true,
  };
}

export function billingSnapshotForAdmission(
  admissionId: string,
  source: BillingDocumentSource,
): ServiceBillingSnapshot {
  return fromDocument(getBillingDocumentByAdmissionAndSource(admissionId, source));
}

export function billingSnapshotForStay(stayId: string): ServiceBillingSnapshot {
  const docs = listBillingDocumentsByStay(stayId, "hospitalization");
  if (docs.length === 0) {
    return fromDocument(null);
  }
  const latest = docs[0]!;
  const totalAmount = docs.reduce((sum, d) => sum + d.subtotal, 0);
  const unpaid = docs.filter((d) => d.status !== "paid" && d.status !== "cancelled");
  const snapshot = fromDocument(latest);
  return {
    ...snapshot,
    status: unpaid.length > 0 ? unpaid[0]!.status : latest.status,
    statusLabel:
      unpaid.length > 0
        ? `${unpaid.length} nuitée(s) en attente`
        : docs.every((d) => d.status === "paid")
          ? "Payé"
          : snapshot.statusLabel,
    nightCount: docs.length,
    totalAmount,
    amount: totalAmount,
  };
}

export function billingSnapshotForLabOrder(labOrderId: string): ServiceBillingSnapshot {
  return fromDocument(getBillingDocumentByLabOrder(labOrderId));
}

export function billingSnapshotForImagingExam(imagingExamId: string): ServiceBillingSnapshot {
  return fromDocument(getBillingDocumentByImagingExam(imagingExamId));
}

export function billingSnapshotForSurgeryCase(surgeryCaseId: string): ServiceBillingSnapshot {
  return fromDocument(getBillingDocumentBySurgeryCase(surgeryCaseId));
}

export function billingSnapshotForConsumableCharge(chargeId: string): ServiceBillingSnapshot {
  return fromDocument(getBillingDocumentByConsumableCharge(chargeId));
}
